'use client';

import {
  requestContactDetailsDescription,
  requestContactDetailsTitle,
  requestDetailsContactMethodDescription,
  requestDetailsContactMethodTitle,
  requestDetailsContactMethodTypesCall,
  requestDetailsContactMethodTypesEmail,
  requestDetailsEmailDescription,
  requestDetailsEmailPlaceholder,
  requestDetailsEmailTitle,
  requestDetailsFirstnameDescription,
  requestDetailsFirstnamePlaceholder,
  requestDetailsFirstnameTitle,
  requestDetailsLastnameDescription,
  requestDetailsLastnamePlaceholder,
  requestDetailsLastnameTitle,
  requestDetailsPhoneDescription,
  requestDetailsPhoneTitle,
  wizardNext,
  wizardPrev,
} from '@bells/i18n';
import { Button } from '@bells/ui/components/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@bells/ui/components/dialog';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@bells/ui/components/form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm } from 'react-hook-form';

import { useRequest } from '@/components/girl/request-context';
import {
  RequestFormSchema,
  type RequestForm,
} from '@bells/schema/models/request';
import { Input } from '@bells/ui/components/input';
import { ResponsiveSelect } from '@bells/ui/components/responsive-select';
import { Skeleton } from '@bells/ui/components/skeleton';

import dynamic from 'next/dynamic';

const PhoneInput = dynamic(() => import('@bells/ui/components/phone-input'), {
  loading: () => <Skeleton className='h-[40px] w-full'></Skeleton>,
});

export const StepChooseContactDetails = ({
  resolve,
  goBack,
}: {
  resolve?: () => void;
  goBack?: () => void;
}) => {
  const request = useRequest();
  const form = useForm<
    Pick<
      RequestForm,
      'firstName' | 'lastName' | 'email' | 'phone' | 'contactMethod'
    >
  >({
    resolver: zodResolver(
      RequestFormSchema.pick({
        firstName: true,
        lastName: true,
        email: true,
        phone: true,
        contactMethod: true,
      })
    ),
    mode: 'all',
    defaultValues: {
      firstName: request.values.firstName,
      lastName: request.values.lastName,
      email: request.values.email,
      phone: request.values.phone,
      contactMethod: request.values.contactMethod,
    },
  });

  const submit = form.handleSubmit((val) => {
    request.setValues(val);

    if (resolve) {
      resolve();
    }
  });

  return (
    <>
      <DialogHeader className='p-6 pb-0'>
        <DialogTitle>{requestContactDetailsTitle()}</DialogTitle>
        <DialogDescription>
          {requestContactDetailsDescription()}
        </DialogDescription>
      </DialogHeader>

      <div className='w-full flex-1 overflow-y-auto p-6'>
        <FormProvider {...form}>
          <form
            className='flex w-full flex-col gap-4 lg:gap-6'
            onSubmit={submit}
          >
            <FormField
              control={form.control}
              name='firstName'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{requestDetailsFirstnameTitle()}</FormLabel>
                  <FormDescription>
                    {requestDetailsFirstnameDescription()}
                  </FormDescription>
                  <FormControl>
                    <Input
                      className=''
                      placeholder={requestDetailsFirstnamePlaceholder()}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='lastName'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{requestDetailsLastnameTitle()}</FormLabel>
                  <FormDescription>
                    {requestDetailsLastnameDescription()}
                  </FormDescription>
                  <FormControl>
                    <Input
                      placeholder={requestDetailsLastnamePlaceholder()}
                      className=''
                      {...field}
                      value={field.value ?? ''}
                      onChange={(ev) => {
                        field.onChange(ev ?? '');
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='email'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{requestDetailsEmailTitle()}</FormLabel>
                  <FormDescription>
                    {requestDetailsEmailDescription()}
                  </FormDescription>
                  <FormControl>
                    <Input
                      placeholder={requestDetailsEmailPlaceholder()}
                      className=''
                      type='email'
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='phone'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{requestDetailsPhoneTitle()}</FormLabel>
                  <FormDescription>
                    {requestDetailsPhoneDescription()}
                  </FormDescription>
                  <FormControl>
                    <PhoneInput
                      defaultCountry='DE'
                      disabled={field.disabled}
                      onBlur={field.onBlur}
                      value={field.value ?? ''}
                      onChange={(ev) => {
                        field.onChange(ev ?? '');
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name='contactMethod'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{requestDetailsContactMethodTitle()}</FormLabel>
                  <FormDescription>
                    {requestDetailsContactMethodDescription()}
                  </FormDescription>
                  <FormControl>
                    <ResponsiveSelect
                      isOptional={false}
                      className='w-full'
                      title={'Type'}
                      disabled={field.disabled}
                      options={{
                        email: requestDetailsContactMethodTypesEmail(),
                        phone: requestDetailsContactMethodTypesCall(),
                      }}
                      value={field.value}
                      onChange={field.onChange}
                    ></ResponsiveSelect>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </FormProvider>
      </div>
      <DialogFooter className='gap-2 p-6 pt-0'>
        <Button onClick={goBack} type='button' variant={'outline'}>
          {wizardPrev()}
        </Button>
        <Button
          disabled={!form.formState.isValid}
          onClick={submit}
          type='submit'
        >
          {wizardNext()}
        </Button>
      </DialogFooter>
    </>
  );
};
